import React, { useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import {
  X,
  Mail,
  Phone,
  Globe,
  Edit2,
  Save,
  MapPin,
  Clock,
} from "lucide-react";
import axios from "axios";

export default function ContactDetailModal({
  open,
  setOpen,
  contact,
  onUpdate,
  currentUserEmail,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContact, setEditedContact] = useState({});
  const [notes, setNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (contact) {
      setEditedContact({ ...contact });
      setNotes(contact.notes || "");
    }
  }, [contact]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updatedContact = {
        ...editedContact,
        notes,
      };

      // Update contact in backend
      const response = await axios.post(
        `${process.env.REACT_APP_PUBLIC_BACKEND_URL}update_contact`,
        {
          email: currentUserEmail,
          contactEmail: contact.email,
          updatedContact,
        }
      );

      if (response.status === 200) {
        onUpdate(updatedContact);
        setIsEditing(false);
      }
    } catch (error) {
      console.error("Error updating contact:", error);
      alert("Failed to update contact. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadVCard = () => {
    const vCard = `BEGIN:VCARD
VERSION:3.0
FN:${editedContact.name}
EMAIL:${editedContact.email}
TEL:${editedContact.phone || ""}
NOTE:${notes}
END:VCARD`;
    const blob = new Blob([vCard], { type: "text/vcard" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${editedContact.name?.replace(/\s/g, "_")}.vcf`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!contact) return null;

  const getAvatarColor = (name) => {
    const colors = [
      "bg-blue-500",
      "bg-indigo-500",
      "bg-purple-500",
      "bg-pink-500",
      "bg-red-500",
      "bg-orange-500",
      "bg-yellow-500",
      "bg-green-500",
      "bg-teal-500",
      "bg-cyan-500",
    ];
    const charCode = name?.charCodeAt(0) || 0;
    return colors[charCode % colors.length];
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all w-full max-w-lg">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                  <Dialog.Title className="text-lg font-medium text-gray-900">
                    Contact Details
                  </Dialog.Title>
                  <button
                    onClick={() => setOpen(false)}
                    className="text-gray-400 hover:text-gray-500 transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Content */}
                <div className="px-6 py-6 space-y-6">
                  {/* Profile Section */}
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-semibold ${getAvatarColor(
                        editedContact.name
                      )}`}
                    >
                      {editedContact.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editedContact.name}
                          onChange={(e) =>
                            setEditedContact({
                              ...editedContact,
                              name: e.target.value,
                            })
                          }
                          autoComplete="name"
                          className="text-xl font-semibold text-gray-900 border-b-2 border-blue-600 focus:outline-none w-full"
                          placeholder="Contact name"
                        />
                      ) : (
                        <h3 className="text-xl font-semibold text-gray-900">
                          {editedContact.name}
                        </h3>
                      )}
                      {contact.mutual && (
                        <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full font-medium border border-green-200 inline-block mt-1">
                          Mutual Connection
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-900">
                      Contact Information
                    </h4>

                    {/* Email */}
                    <div className="flex items-center gap-3 group">
                      <Mail size={18} className="text-gray-400" />
                      <div className="flex-1">
                        {isEditing ? (
                          <input
                            type="email"
                            value={editedContact.email}
                            onChange={(e) =>
                              setEditedContact({
                                ...editedContact,
                                email: e.target.value,
                              })
                            }
                            autoComplete="email"
                            className="text-sm text-gray-900 border-b border-gray-300 focus:border-blue-600 focus:outline-none w-full"
                            placeholder="Email address"
                          />
                        ) : (
                          <a
                            href={`mailto:${editedContact.email}`}
                            className="text-sm text-blue-600 hover:underline"
                          >
                            {editedContact.email}
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="flex items-center gap-3 group">
                      <Phone size={18} className="text-gray-400" />
                      <div className="flex-1">
                        {isEditing ? (
                          <input
                            type="tel"
                            value={editedContact.phone || ""}
                            onChange={(e) =>
                              setEditedContact({
                                ...editedContact,
                                phone: e.target.value,
                              })
                            }
                            autoComplete="tel"
                            className="text-sm text-gray-900 border-b border-gray-300 focus:border-blue-600 focus:outline-none w-full"
                            placeholder="Phone number"
                          />
                        ) : editedContact.phone ? (
                          <a
                            href={`tel:${editedContact.phone}`}
                            className="text-sm text-blue-600 hover:underline"
                          >
                            {editedContact.phone}
                          </a>
                        ) : (
                          <span className="text-sm text-gray-400 italic">
                            No phone number
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Location */}
                    {contact.location?.city && (
                      <div className="flex items-center gap-3">
                        <MapPin size={18} className="text-gray-400" />
                        <span className="text-sm text-gray-600">
                          Met in {contact.location.city}
                          {contact.location.state &&
                            `, ${contact.location.state}`}
                        </span>
                      </div>
                    )}

                    {/* Date Added */}
                    {contact.timestamp && (
                      <div className="flex items-center gap-3">
                        <Clock size={18} className="text-gray-400" />
                        <span className="text-sm text-gray-600">
                          Added on{" "}
                          {new Date(contact.timestamp).toLocaleDateString(
                            "en-US",
                            {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                            }
                          )}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Notes Section */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-900 block">
                      Notes
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add notes about this contact..."
                      rows={4}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                  <button
                    onClick={handleDownloadVCard}
                    className="text-sm text-gray-600 hover:text-gray-900 font-medium"
                  >
                    Download vCard
                  </button>
                  <div className="flex gap-2">
                    {isEditing ? (
                      <>
                        <button
                          onClick={() => {
                            setIsEditing(false);
                            setEditedContact({ ...contact });
                            setNotes(contact.notes || "");
                          }}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSave}
                          disabled={isSaving}
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                        >
                          {isSaving ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save size={16} />
                              Save
                            </>
                          )}
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                      >
                        <Edit2 size={16} />
                        Edit
                      </button>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
